import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import SessionManager from '../../components/ui/SessionManager';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BrandingFooter from '../../components/ui/BrandingFooter';
import { useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

const EnhancedDashboardHomeWithAlertManagement = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [quickStats, setQuickStats] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);

  useEffect(() => {
    setAlerts([
      {
        id: 1,
        type: 'expired-certs',
        title: 'Expired Certificates',
        count: 13,
        color: 'var(--color-error)',
        icon: 'AlertCircle',
        route: '/smart-certificate-expiry-management',
        description: 'Immediate action required'
      },
      {
        id: 2,
        type: 'expiring-soon',
        title: 'Expiring Soon (0-14 days)',
        count: 22,
        color: 'var(--color-error)',
        icon: 'Clock',
        route: '/smart-certificate-expiry-management',
        description: 'Critical renewal window'
      },
      {
        id: 3,
        type: 'expiring-30',
        title: 'Expiring (15-30 days)',
        count: 13,
        color: 'var(--color-warning)',
        icon: 'Calendar',
        route: '/smart-certificate-expiry-management',
        description: 'Plan renewal soon'
      },
      {
        id: 4,
        type: 'renewals-due',
        title: 'Tenancy Renewals Due',
        count: 9,
        color: 'var(--color-error)',
        icon: 'Users',
        route: '/properties-management-screen',
        description: '0-14 days until renewal'
      },
      {
        id: 5,
        type: 'renewals-upcoming',
        title: 'Upcoming Renewals',
        count: 14,
        color: 'var(--color-warning)',
        icon: 'FileText',
        route: '/properties-management-screen',
        description: '15-30 days until renewal'
      },
      {
        id: 6,
        type: 'maintenance-open',
        title: 'Open Maintenance Jobs',
        count: 34,
        color: 'var(--color-warning)',
        icon: 'Wrench',
        route: '/maintenance-kanban-workflow-board',
        description: '5 urgent priority'
      },
      {
        id: 7,
        type: 'vacant-properties',
        title: 'Vacant Properties',
        count: 18,
        color: 'var(--color-accent)',
        icon: 'Home',
        route: '/properties-management-screen',
        description: 'Available for letting'
      },
      {
        id: 8,
        type: 'incomplete-docs',
        title: 'Incomplete Tenant Documents',
        count: 7,
        color: 'var(--color-error)',
        icon: 'AlertTriangle',
        route: '/tenant-onboarding-management-screen',
        description: 'Missing required documents'
      }
    ]);

    setQuickStats([
      {
        label: 'Total Properties',
        value: '247',
        icon: 'Building2',
        color: 'var(--color-accent)',
        trend: '+12',
        trendLabel: 'this month'
      },
      {
        label: 'Occupancy Rate',
        value: '92.7%',
        icon: 'TrendingUp',
        color: 'var(--color-success)',
        trend: '+2.3%',
        trendLabel: 'from last month'
      },
      {
        label: 'Monthly Revenue',
        value: '£284,500',
        icon: 'PoundSterling',
        color: 'var(--color-success)',
        trend: '+£18,200',
        trendLabel: 'vs last month'
      },
      {
        label: 'Compliance Score',
        value: '96%',
        icon: 'ShieldCheck',
        color: 'var(--color-success)',
        trend: '+4%',
        trendLabel: 'improvement'
      },
      {
        label: 'Active Tenancies',
        value: '229',
        icon: 'Users',
        color: 'var(--color-accent)',
        trend: '+8',
        trendLabel: 'new this month'
      },
      {
        label: 'Maintenance Backlog',
        value: '34',
        icon: 'Wrench',
        color: 'var(--color-warning)',
        trend: '+5',
        trendLabel: 'since yesterday'
      }
    ]);

    setUpcomingDeadlines([
      {
        id: 1,
        type: 'certificate',
        title: 'Gas Safety Certificate - P001',
        address: '123 High Street, London',
        dueDate: '2026-02-18',
        daysUntil: 2,
        priority: 'urgent',
        icon: 'Flame'
      },
      {
        id: 2,
        type: 'certificate',
        title: 'EPC Certificate - P002',
        address: '45 Park Avenue, Manchester',
        dueDate: '2026-02-28',
        daysUntil: 12,
        priority: 'high',
        icon: 'Zap'
      },
      {
        id: 3,
        type: 'tenancy',
        title: 'Tenancy Renewal - Sarah Johnson',
        address: '123 High Street, London',
        dueDate: '2026-02-25',
        daysUntil: 9,
        priority: 'high',
        icon: 'Users'
      },
      {
        id: 4,
        type: 'certificate',
        title: 'EICR Certificate - P003',
        address: '78 Queen Road, Birmingham',
        dueDate: '2026-03-10',
        daysUntil: 22,
        priority: 'medium',
        icon: 'Plug'
      },
      {
        id: 5,
        type: 'maintenance',
        title: 'Boiler Service - P004',
        address: '12 Victoria Street, Leeds',
        dueDate: '2026-03-05',
        daysUntil: 17,
        priority: 'medium',
        icon: 'Wrench'
      },
      {
        id: 6,
        type: 'tenancy',
        title: 'Tenancy Renewal - Michael Brown',
        address: '45 Park Avenue, Manchester',
        dueDate: '2026-03-01',
        daysUntil: 13,
        priority: 'high',
        icon: 'Users'
      }
    ]);
  }, []);

  const getPriorityColor = (priority) => {
    if (priority === 'urgent') return 'var(--color-error)';
    if (priority === 'high') return 'var(--color-warning)';
    if (priority === 'medium') return 'var(--color-accent)';
    return 'var(--color-success)';
  };

  const dismissAlert = (alertId) => {
    setAlerts(alerts?.filter(a => a?.id !== alertId));
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" />
      <SessionManager />
      <Header />
      <main className="pt-[108px] px-4 md:px-6 lg:px-8 pb-8">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-semibold text-foreground font-heading mb-2">Dashboard Overview</h1>
            <p className="text-muted-foreground">Comprehensive property management command center</p>
          </div>

          <div className="mb-6 p-6 rounded-lg bg-gradient-to-r from-error/10 via-warning/10 to-accent/10 border border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-error/20 flex items-center justify-center">
                <Icon name="Bell" size={20} color="var(--color-error)" />
              </div>
              <h2 className="text-xl font-semibold text-foreground font-heading">Alert Summary</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {alerts?.map(alert => (
                <div
                  key={alert?.id}
                  className="p-4 rounded-lg bg-surface border border-border transition-smooth hover:shadow-warm-md cursor-pointer group"
                  onClick={() => navigate(alert?.route)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${alert?.color}20` }}
                    >
                      <Icon name={alert?.icon} size={20} color={alert?.color} />
                    </div>
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        dismissAlert(alert?.id);
                      }}
                      className="h-6 w-6 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-input"
                      aria-label="Dismiss"
                    >
                      <Icon name="X" size={14} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                  <div
                    className="text-3xl font-bold mb-2"
                    style={{ color: alert?.color }}
                  >
                    {alert?.count}
                  </div>
                  <p className="text-sm font-medium text-foreground mb-1">{alert?.title}</p>
                  <p className="text-xs text-muted-foreground">{alert?.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-foreground font-heading mb-4">Quick Stats</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quickStats?.map((stat, index) => (
                <div
                  key={index}
                  className="card transition-smooth hover:shadow-warm-lg"
                  style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="h-12 w-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${stat?.color}20` }}
                    >
                      <Icon name={stat?.icon} size={24} color={stat?.color} />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-foreground mb-2">{stat?.value}</div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">{stat?.label}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold" style={{ color: stat?.color }}>
                      {stat?.trend}
                    </span>
                    <span className="text-xs text-muted-foreground">{stat?.trendLabel}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold text-foreground font-heading">Upcoming Deadlines (Next 30 Days)</h2>
              <Button variant="outline" size="sm" iconName="Calendar" iconPosition="left">
                View Calendar
              </Button>
            </div>
            
            <div className="card">
              <div className="space-y-3">
                {upcomingDeadlines?.map((deadline, index) => (
                  <div
                    key={deadline?.id}
                    className={`p-4 rounded-lg bg-input border border-border transition-smooth hover:bg-muted cursor-pointer ${
                      index !== upcomingDeadlines?.length - 1 ? 'border-b' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${getPriorityColor(deadline?.priority)}20` }}
                      >
                        <Icon name={deadline?.icon} size={20} color={getPriorityColor(deadline?.priority)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-foreground mb-1">{deadline?.title}</h3>
                            <p className="text-xs text-muted-foreground">{deadline?.address}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div
                              className="px-3 py-1 rounded text-xs font-semibold mb-1"
                              style={{
                                backgroundColor: `${getPriorityColor(deadline?.priority)}20`,
                                color: getPriorityColor(deadline?.priority)
                              }}
                            >
                              {deadline?.daysUntil} days
                            </div>
                            <p className="text-xs text-muted-foreground">{deadline?.dueDate}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Branding Footer */}
      <BrandingFooter />
    </div>
  );
};

export default EnhancedDashboardHomeWithAlertManagement;